import { Investment, InvestmentResults } from '../types';
import { getDetailedCertificateEvolution } from './calculations/certificateEvolution';

function shouldUseCompoundInterest(investment: Investment): boolean {
  if (!investment.reinvestProfits) {
    return false;
  }

  if (investment.partialCashOut) {
    const avgCashOutPercentage = investment.yearlyCashOutPercentages.length > 0
      ? investment.yearlyCashOutPercentages.reduce((sum, p) => sum + p, 0) / investment.yearlyCashOutPercentages.length
      : investment.cashOutPercentage;

    if (avgCashOutPercentage >= 95) {
      return false;
    }
  }

  return true;
}

function calculateAlternativeInvestmentPatrimony(
  initialInvestment: number,
  rate: number,
  year: number,
  investment: Investment
): number {
  const useCompound = shouldUseCompoundInterest(investment);

  if (!useCompound) {
    return initialInvestment + (initialInvestment * (rate / 100) * year);
  }

  if (investment.partialCashOut) {
    let patrimony = initialInvestment;

    for (let y = 1; y <= year; y++) {
      const yearIndex = Math.min(y - 1, investment.yearlyCashOutPercentages.length - 1);
      const cashOutPercentage = investment.yearlyCashOutPercentages[yearIndex] || investment.cashOutPercentage;
      const reinvestPercentage = 100 - cashOutPercentage;

      const yearlyReturn = patrimony * (rate / 100);
      const reinvestedAmount = yearlyReturn * (reinvestPercentage / 100);
      patrimony += reinvestedAmount;
    }

    return patrimony;
  }

  return initialInvestment * Math.pow(1 + rate / 100, year);
}

function calculateAlternativeInvestmentIncome(
  patrimony: number,
  initialInvestment: number,
  rate: number,
  investment: Investment
): number {
  const useCompound = shouldUseCompoundInterest(investment);

  if (!useCompound) {
    return initialInvestment * (rate / 100);
  }

  return patrimony * (rate / 100);
}

export function calculateResults(investment: Investment): InvestmentResults {
  try {
    // Get detailed certificate evolution
    const certificateEvolution = getDetailedCertificateEvolution(investment);
    
    if (!certificateEvolution || certificateEvolution.length === 0) {
      throw new Error('No certificate evolution data available');
    }

    // Get final year data
    const finalYearData = certificateEvolution[certificateEvolution.length - 1];
    
    // Calculate basic metrics
    const initialInvestment = investment.initialCertificates * investment.certificateBasePrice;
    const finalPatrimony = finalYearData.citrusPatrimony;
    const finalMonthlyIncome = finalYearData.citrusIncome / 12;
    
    // Calculate certificates summary
    const certificatesSummary = {
      initialCertificates: investment.initialCertificates,
      fromReinvestment: finalYearData.certificatesFromReinvestment,
      totalCertificates: finalYearData.totalCertificates
    };
    
    // Calculate performance metrics
    const capitalMultiplier = finalPatrimony / initialInvestment;
    const years = investment.years;
    const cagr = Math.pow(finalPatrimony / initialInvestment, 1 / years) - 1;
    
    // Calculate IRR (simplified)
    const irr = cagr; // Simplified calculation
    
    // Find payback year
    let paybackYear: number | null = null;
    let cumulativeCashFlow = -initialInvestment;
    
    for (const yearData of certificateEvolution) {
      cumulativeCashFlow += yearData.totalUtility;
      if (cumulativeCashFlow > 0 && !paybackYear) {
        paybackYear = yearData.year;
        break;
      }
    }
    
    // Calculate comparative investments considering cash out percentage
    const cetesPatrimony = calculateAlternativeInvestmentPatrimony(
      initialInvestment,
      investment.cetesRate,
      years,
      investment
    );
    const savingsPatrimony = calculateAlternativeInvestmentPatrimony(
      initialInvestment,
      investment.savingsRate,
      years,
      investment
    );
    const realEstatePatrimony = calculateAlternativeInvestmentPatrimony(
      initialInvestment,
      investment.realEstateRate || investment.realEstateAppreciation,
      years,
      investment
    );
    const customInvestmentPatrimony = calculateAlternativeInvestmentPatrimony(
      initialInvestment,
      investment.customInvestmentRate || 8.0,
      years,
      investment
    );

    // Calculate real values (inflation-adjusted)
    const inflationFactor = Math.pow(1 + investment.inflationRate / 100, years);
    const realFinalPatrimony = finalPatrimony / inflationFactor;
    const realCetesPatrimony = cetesPatrimony / inflationFactor;
    const realSavingsPatrimony = savingsPatrimony / inflationFactor;
    const realRealEstatePatrimony = realEstatePatrimony / inflationFactor;
    const realCustomInvestmentPatrimony = customInvestmentPatrimony / inflationFactor;
    
    // Calculate EBITDA value
    const finalEbitdaValue = finalYearData.totalUtility * investment.ebitdaFactor;
    
    // Calculate total cash out if partial cash out is enabled
    const totalCashOut = investment.partialCashOut ? 
      certificateEvolution.reduce((sum, data) => sum + data.yearlyCashOutAmount, 0) : 0;
    
    // Calculate calculated annual profit percentage
    const calculatedAnnualProfitPercentage = 
      (investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100;
    
    // Calculate first year utility and price differential for long-term calculator
    const firstYearUtility = investment.isLongTermCalculator ? 
      (investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65) : 0;
    const priceDifferential = investment.isLongTermCalculator ? 
      investment.certificateBasePrice * 0.25 : 0;
    
    // Prepare yearly data
    const yearlyData = certificateEvolution.map(data => ({
      year: data.year,
      citrusPatrimony: data.citrusPatrimony,
      citrusIncome: data.citrusIncome,
      totalCertificates: data.totalCertificates,
      certificatesFromReinvestment: data.certificatesFromReinvestment,
      reinvestmentFund: data.reinvestmentFund,
      availableCashFlow: data.totalUtility - (data.totalUtility || 0),
      partialCashOutAmount: data.yearlyCashOutAmount,
      yearlyReinvestmentContribution: data.yearlyReinvestmentContribution,
      cumulativeReinvestmentContribution: data.cumulativeReinvestmentContribution,
      cumulativeTotalUtility: data.cumulativeTotalUtility,
      contributedCapital: initialInvestment,
      
      // Calculate comparative investments for this year considering cash out percentage
      cetesPatrimony: calculateAlternativeInvestmentPatrimony(
        initialInvestment,
        investment.cetesRate,
        data.year,
        investment
      ),
      savingsPatrimony: calculateAlternativeInvestmentPatrimony(
        initialInvestment,
        investment.savingsRate,
        data.year,
        investment
      ),
      realEstatePatrimony: calculateAlternativeInvestmentPatrimony(
        initialInvestment,
        investment.realEstateRate || investment.realEstateAppreciation,
        data.year,
        investment
      ),
      customInvestmentPatrimony: calculateAlternativeInvestmentPatrimony(
        initialInvestment,
        investment.customInvestmentRate || 8.0,
        data.year,
        investment
      ),

      // Calculate income from comparative investments considering cash out percentage
      cetesIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.cetesRate, data.year, investment);
        return calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.cetesRate, investment);
      })(),
      savingsIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.savingsRate, data.year, investment);
        return calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.savingsRate, investment);
      })(),
      realEstateIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.realEstateRate || investment.realEstateAppreciation, data.year, investment);
        return calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.realEstateRent, investment);
      })(),
      customInvestmentIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.customInvestmentRate || 8.0, data.year, investment);
        return calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.customInvestmentRate || 8.0, investment);
      })(),

      // Calculate taxes if applicable
      cetesTaxes: (() => {
        if (!investment.applyTaxes) return 0;
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.cetesRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.cetesRate, investment);
        return income * (investment.taxRate / 100);
      })(),
      savingsTaxes: (() => {
        if (!investment.applyTaxes) return 0;
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.savingsRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.savingsRate, investment);
        return income * (investment.taxRate / 100);
      })(),
      realEstateTaxes: (() => {
        if (!investment.applyTaxes) return 0;
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.realEstateRate || investment.realEstateAppreciation, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.realEstateRent, investment);
        return income * (investment.taxRate / 100);
      })(),
      customInvestmentTaxes: (() => {
        if (!investment.applyTaxes) return 0;
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.customInvestmentRate || 8.0, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.customInvestmentRate || 8.0, investment);
        return income * (investment.taxRate / 100);
      })(),

      // Calculate real values (inflation-adjusted)
      realCitrusPatrimony: data.citrusPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year),
      realCetesPatrimony: calculateAlternativeInvestmentPatrimony(initialInvestment, investment.cetesRate, data.year, investment) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realSavingsPatrimony: calculateAlternativeInvestmentPatrimony(initialInvestment, investment.savingsRate, data.year, investment) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realEstateRealPatrimony: calculateAlternativeInvestmentPatrimony(initialInvestment, investment.realEstateRate || investment.realEstateAppreciation, data.year, investment) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realCustomInvestmentPatrimony: calculateAlternativeInvestmentPatrimony(initialInvestment, investment.customInvestmentRate || 8.0, data.year, investment) / Math.pow(1 + investment.inflationRate / 100, data.year),

      // Calculate real income
      realCitrusIncome: data.citrusIncome / Math.pow(1 + investment.inflationRate / 100, data.year),
      realCetesIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.cetesRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.cetesRate, investment);
        return income / Math.pow(1 + investment.inflationRate / 100, data.year);
      })(),
      realSavingsIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.savingsRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.savingsRate, investment);
        return income / Math.pow(1 + investment.inflationRate / 100, data.year);
      })(),
      realEstateRealIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.realEstateRate || investment.realEstateAppreciation, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.realEstateRent, investment);
        return income / Math.pow(1 + investment.inflationRate / 100, data.year);
      })(),
      realCustomInvestmentIncome: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.customInvestmentRate || 8.0, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.customInvestmentRate || 8.0, investment);
        return income / Math.pow(1 + investment.inflationRate / 100, data.year);
      })(),

      // EBITDA value
      ebitdaValue: data.totalUtility * investment.ebitdaFactor,
      
      // Cash flow calculations
      cetesCashFlow: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.cetesRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.cetesRate, investment);
        return data.year === 1 ? -initialInvestment + income : income;
      })(),
      savingsCashFlow: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.savingsRate, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.savingsRate, investment);
        return data.year === 1 ? -initialInvestment + income : income;
      })(),
      realEstateCashFlow: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.realEstateRate || investment.realEstateAppreciation, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.realEstateRent, investment);
        return data.year === 1 ? -initialInvestment + income : income;
      })(),
      customInvestmentCashFlow: (() => {
        const patrimony = calculateAlternativeInvestmentPatrimony(initialInvestment, investment.customInvestmentRate || 8.0, data.year, investment);
        const income = calculateAlternativeInvestmentIncome(patrimony, initialInvestment, investment.customInvestmentRate || 8.0, investment);
        return data.year === 1 ? -initialInvestment + income : income;
      })(),

      // Additional fields for partial cash out
      yearCashOutPercentage: investment.partialCashOut ? 
        (investment.yearlyCashOutPercentages && investment.yearlyCashOutPercentages[data.year - 1] !== undefined ? 
          investment.yearlyCashOutPercentages[data.year - 1] : investment.cashOutPercentage) : 0
    }));
    
    // Calculate passive income efficiency
    const passiveIncomeEfficiency = finalMonthlyIncome / (finalPatrimony * 0.01); // Efficiency metric

    // Calculate monthly income for comparative investments (from final year)
    const finalYearComparative = yearlyData[yearlyData.length - 1];
    const cetesMonthlyIncome = finalYearComparative.cetesIncome / 12;
    const savingsMonthlyIncome = finalYearComparative.savingsIncome / 12;
    const realEstateMonthlyIncome = finalYearComparative.realEstateIncome / 12;

    // Calculate total profit and ROI
    const totalProfit = finalPatrimony - initialInvestment;
    const roi = ((finalPatrimony - initialInvestment) / initialInvestment) * 100;

    return {
      finalMonthlyIncome,
      finalPatrimony,
      capitalMultiplier,
      cagr,
      irr,
      paybackYear,
      certificatesSummary,
      passiveIncomeEfficiency,
      cetesPatrimony,
      savingsPatrimony,
      realEstatePatrimony,
      customInvestmentPatrimony,
      cetesMonthlyIncome,
      savingsMonthlyIncome,
      realEstateMonthlyIncome,
      customInvestmentMonthlyIncome: customInvestmentPatrimony * ((investment.customInvestmentRate || 8.0) / 100) / 12,
      realFinalPatrimony,
      realCetesPatrimony,
      realSavingsPatrimony,
      realRealEstatePatrimony,
      realCustomInvestmentPatrimony,
      finalEbitdaValue,
      totalCashOut,
      calculatedAnnualProfitPercentage,
      firstYearUtility,
      priceDifferential,
      totalProfit,
      roi,
      yearlyData
    };
  } catch (error) {
    console.error('Error in calculateResults:', error);
    throw error;
  }
}